/**
 * Depósito de retiro en remito de ventas (parcial/final).
 * Combo: preferir mismo depósito si ambas SKU cubren ahí; si no, picker por SKU.
 * Run: npx tsx --test src/modules/RemitosVtas/remitoDepositoRetiro.test.ts
 */
import { toNum } from "../../lib/utils";
import type {
  VentaRemitoDepositoOpcion,
  VentaRemitoEntregaItem,
  VentaRemitoItemOpcionesDeposito,
  VentaRemitoOpcionesDeposito,
} from "../../lib/api";

export function depStock(dep: { cantidad?: string | number; stock?: string | number }) {
  return toNum(dep.stock ?? dep.cantidad);
}

export function itemQtyNeeded(row: VentaRemitoItemOpcionesDeposito) {
  return toNum(row.cantidad);
}

export function coveringDepositos(row: VentaRemitoItemOpcionesDeposito) {
  const q = itemQtyNeeded(row);
  return (row.depositos || []).filter((dep) => depStock(dep) >= q);
}

export type DepositoRetiroModo = "auto" | "radio" | "combinar" | "insuficiente";

export function depositoRetiroModo(row: VentaRemitoItemOpcionesDeposito): DepositoRetiroModo {
  const deps = row.depositos || [];
  const q = itemQtyNeeded(row);
  const covering = coveringDepositos(row);
  const suma = deps.reduce((acc, dep) => acc + depStock(dep), 0);
  const puedeSolo = row.puedeCubrirSolo ?? covering.length > 0;
  const sumaCubre = row.sumaCubre ?? suma >= q;
  if (!sumaCubre) return "insuficiente";
  if (puedeSolo) {
    return covering.length > 1 ? "radio" : "auto";
  }
  return "combinar";
}

export function sortDepositosForSplit<T extends { id: number; nombre?: string }>(deps: T[]): T[] {
  return [...deps].sort((a, b) => {
    const byName = String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", {
      sensitivity: "base",
    });
    if (byName !== 0) return byName;
    return a.id - b.id;
  });
}

export function splitQtyAcrossDepositos(
  deps: VentaRemitoDepositoOpcion[],
  qty: number,
): { id: number; cantidad: number }[] {
  const sorted = sortDepositosForSplit(deps);
  let remaining = qty;
  const out: { id: number; cantidad: number }[] = [];
  for (const dep of sorted) {
    if (remaining <= 0) break;
    const avail = depStock(dep);
    const take = remaining < avail ? remaining : avail;
    if (take > 0) {
      out.push({ id: dep.id, cantidad: take });
      remaining -= take;
    }
  }
  return out;
}

export function checkedStockSum(row: VentaRemitoItemOpcionesDeposito, checkedIds: number[]) {
  const selected = new Set(checkedIds);
  return (row.depositos || [])
    .filter((dep) => selected.has(dep.id))
    .reduce((acc, dep) => acc + depStock(dep), 0);
}

export function attachDepositoIdsToItems(
  items: VentaRemitoEntregaItem[],
  opciones: VentaRemitoOpcionesDeposito | null,
  radioChosen: Record<number, number>,
  checked: Record<number, number[]>,
): VentaRemitoEntregaItem[] {
  const byId = new Map((opciones?.items || []).map((row) => [row.id, row]));
  const needPicker = new Set(itemsNeedingDepositoChoice(opciones).map((row) => row.id));
  return items.map((item) => {
    const row = byId.get(item.id);
    if (!row) return item;
    const modo = depositoRetiroModo(row);
    const q = itemQtyNeeded(row);
    const radioId = radioChosen[item.id];
    // Combo: radio elegido de ESTA pata; nunca el de la hermana.
    if (needPicker.has(item.id) && modo !== "combinar") {
      const id = radioId ?? (modo === "auto" ? coveringDepositos(row)[0]?.id : undefined);
      if (id == null) return item;
      return {
        ...item,
        depositoId: id,
        depositos: [{ id, cantidad: q }],
      };
    }
    if (modo === "auto") {
      const chosen = coveringDepositos(row)[0];
      if (chosen?.id == null) return item;
      return {
        ...item,
        depositoId: chosen.id,
        depositos: [{ id: chosen.id, cantidad: q }],
      };
    }
    if (modo === "radio") {
      const id = radioChosen[item.id];
      if (id == null) return item;
      return {
        ...item,
        depositoId: id,
        depositos: [{ id, cantidad: q }],
      };
    }
    if (modo === "combinar") {
      const ids = checked[item.id] || [];
      const selected = (row.depositos || []).filter((dep) => ids.includes(dep.id));
      const depositos = splitQtyAcrossDepositos(selected, q);
      return {
        ...item,
        depositoIds: ids,
        depositos,
      };
    }
    return item;
  });
}

export function comboRowsByGroup(
  opciones: VentaRemitoOpcionesDeposito | null,
): Map<string, VentaRemitoItemOpcionesDeposito[]> {
  const groups = new Map<string, VentaRemitoItemOpcionesDeposito[]>();
  for (const row of opciones?.items || []) {
    const g = comboGroupOfOpcionesRow(row);
    if (!g) continue;
    const list = groups.get(g) || [];
    list.push(row);
    groups.set(g, list);
  }
  return groups;
}

/** Mismo depósito único que cubre ambas patas: no hay que preguntar. */
export function comboGroupSharesUniqueDeposito(rows: VentaRemitoItemOpcionesDeposito[]): boolean {
  if (rows.length < 2) return false;
  const shared = sharedCoveringDepositoIds(rows);
  if (shared.length !== 1) return false;
  return rows.every((row) => coveringDepositos(row).length === 1);
}

export function itemsNeedingDepositoChoice(opciones: VentaRemitoOpcionesDeposito | null) {
  const items = opciones?.items || [];
  const need = new Set<number>();
  for (const row of items) {
    const modo = depositoRetiroModo(row);
    if (modo === "radio" || modo === "combinar") need.add(row.id);
  }
  for (const rows of comboRowsByGroup(opciones).values()) {
    if (rows.length < 2) continue;
    if (comboGroupSharesUniqueDeposito(rows)) continue;
    for (const row of rows) {
      if (depositoRetiroModo(row) === "insuficiente") continue;
      if ((row.depositos || []).length > 0) need.add(row.id);
    }
  }
  return items.filter((row) => need.has(row.id));
}

export function itemsDepositoInsuficiente(opciones: VentaRemitoOpcionesDeposito | null) {
  return (opciones?.items || []).filter((row) => depositoRetiroModo(row) === "insuficiente");
}

export function isDepositoRowSelectionValid(
  row: VentaRemitoItemOpcionesDeposito,
  radioChosen: Record<number, number>,
  checked: Record<number, number[]>,
) {
  const modo = depositoRetiroModo(row);
  if (modo === "radio") return radioChosen[row.id] != null;
  if (modo === "combinar") {
    return checkedStockSum(row, checked[row.id] || []) >= itemQtyNeeded(row);
  }
  if (modo === "auto") {
    return radioChosen[row.id] != null;
  }
  return true;
}

export function comboGroupOfOpcionesRow(
  row: Pick<VentaRemitoItemOpcionesDeposito, "comboGroup" | "combo_group">,
): string | null {
  const g = row.comboGroup ?? row.combo_group;
  if (g == null || g === "") return null;
  return String(g);
}

/** Depósitos que cubren Q en todas las patas del combo (mismo depósito preferido). */
export function sharedCoveringDepositoIds(rows: VentaRemitoItemOpcionesDeposito[]): number[] {
  if (rows.length === 0) return [];
  let shared: Set<number> | null = null;
  for (const row of rows) {
    const ids = new Set(coveringDepositos(row).map((d) => d.id));
    shared = shared == null ? ids : new Set([...shared].filter((id) => ids.has(id)));
  }
  return [...(shared || [])].sort((a, b) => a - b);
}

/**
 * Preselección por pata: solo el depósito único DE ESA SKU.
 * No copiar el elegido de A sobre B.
 */
export function initialRadioChoiceForCombo(
  opciones: VentaRemitoOpcionesDeposito | null,
): Record<number, number> {
  const choice: Record<number, number> = {};
  for (const row of opciones?.items || []) {
    const covering = coveringDepositos(row);
    if (covering.length === 1) {
      choice[row.id] = covering[0].id;
    }
  }
  return choice;
}

export type EntregaDepositoDecision = "commit" | "picker" | "insuficiente";

/** No commitear si hace falta input: radio/combinar gana a insuficiente (no toast-only). */
export function entregaDepositoDecision(
  opciones: VentaRemitoOpcionesDeposito | null,
): EntregaDepositoDecision {
  if (itemsNeedingDepositoChoice(opciones).length > 0) return "picker";
  if (itemsDepositoInsuficiente(opciones).length > 0) return "insuficiente";
  return "commit";
}

export function mergeComboGroupOntoOpciones(
  opciones: VentaRemitoOpcionesDeposito,
  groupsByItemId: Record<number, string | null | undefined>,
): VentaRemitoOpcionesDeposito {
  return {
    ...opciones,
    items: (opciones.items || []).map((row) => {
      const fromApi = comboGroupOfOpcionesRow(row);
      const fromRemito = groupsByItemId[row.id];
      const comboGroup = fromApi ?? (fromRemito != null && fromRemito !== "" ? String(fromRemito) : null);
      return { ...row, comboGroup };
    }),
  };
}

function readErrorBag(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== "object") return {};
  const err = error as {
    error?: unknown;
    data?: Record<string, unknown>;
    details?: Record<string, unknown> | string;
  };
  const bags: Record<string, unknown>[] = [];
  if (err.data && typeof err.data === "object") bags.push(err.data);
  if (err.details && typeof err.details === "object") bags.push(err.details);
  bags.push(err as Record<string, unknown>);
  const merged: Record<string, unknown> = {};
  for (const bag of bags) {
    for (const [k, v] of Object.entries(bag)) {
      if (merged[k] == null && v != null) merged[k] = v;
    }
  }
  return merged;
}

export function isStockScopeAmbiguoError(error: unknown): boolean {
  const bag = readErrorBag(error);
  return bag.error === "stock_scope_ambiguo";
}

export function isStockNoDisponibleError(error: unknown): boolean {
  const bag = readErrorBag(error);
  return bag.error === "stock_no_disponible";
}

export function itemIdFromAmbiguoError(error: unknown): number | null {
  const bag = readErrorBag(error);
  const raw = bag.itemId ?? bag.item_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function depositosFromAmbiguoError(error: unknown): VentaRemitoDepositoOpcion[] {
  const bag = readErrorBag(error);
  const raw = bag.depositos;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d) => d && typeof d === "object")
    .map((d) => {
      const row = d as Record<string, unknown>;
      return {
        id: Number(row.id),
        nombre: String(row.nombre ?? ""),
        cantidad: (row.cantidad ?? row.stock ?? "0") as string | number,
        stock: (row.stock ?? row.cantidad) as string | number | undefined,
        puedeCubrirSolo: Boolean(row.puedeCubrirSolo ?? row.puede_cubrir_solo),
      };
    })
    .filter((d) => Number.isFinite(d.id));
}

/** Si opciones no pide input pero el 400 sí trae depósitos, armar fila de radio. */
export function opcionesWithAmbiguoFallback(
  opciones: VentaRemitoOpcionesDeposito | null,
  error: unknown,
  qtyByItemId: Record<number, number>,
): VentaRemitoOpcionesDeposito | null {
  if (!isStockScopeAmbiguoError(error) && !isStockNoDisponibleError(error)) return opciones;
  const itemId = itemIdFromAmbiguoError(error);
  const depositos = depositosFromAmbiguoError(error);
  if (itemId == null) return opciones;
  if (depositos.length < 1) return opciones;
  const existing = opciones?.items || [];
  const qty = qtyByItemId[itemId] ?? toNum(existing.find((r) => r.id === itemId)?.cantidad) ?? 1;
  const covering = depositos.filter((d) => depStock(d) >= qty);
  const patched: VentaRemitoItemOpcionesDeposito = {
    ...(existing.find((r) => r.id === itemId) || {
      id: itemId,
      nombreProducto: "",
    }),
    id: itemId,
    cantidad: String(qty),
    depositos,
    puedeCubrirSolo: covering.length > 0,
    sumaCubre: depositos.reduce((acc, d) => acc + depStock(d), 0) >= qty,
  };
  const items = existing.some((r) => r.id === itemId)
    ? existing.map((r) => (r.id === itemId ? { ...r, ...patched, depositos } : r))
    : [...existing, patched];
  return {
    sucursalId: opciones?.sucursalId,
    sucursalNombre: opciones?.sucursalNombre,
    items,
  };
}
